import { getPositionLevel } from '../compensationPartner';

/**
 * Sorts positions by hierarchy level (Junior -> Senior -> Head)
 * @param {array} positions - Array of position objects
 * @returns {array} Sorted positions
 */
export function sortPositionsByLevel(positions) {
  return [...positions].sort((a, b) => {
    const levelA = getPositionLevel(a.positionsBezeichnung);
    const levelB = getPositionLevel(b.positionsBezeichnung);
    return levelA - levelB;
  });
}

/**
 * Filters positions by department
 * @param {array} positions - Array of position objects
 * @param {string} department - Department to filter by ('Alle' for all)
 * @returns {array} Filtered positions
 */
export function filterPositionsByDepartment(positions, department) {
  if (department === 'Alle') {
    return positions;
  }
  return positions.filter(pos => pos.department === department);
}

/**
 * Groups positions by department
 * @param {array} positions - Array of position objects
 * @returns {object} Positions grouped by department
 */
export function groupPositionsByDepartment(positions) {
  return positions.reduce((groups, position) => {
    const dept = position.department;
    if (!groups[dept]) {
      groups[dept] = [];
    }
    groups[dept].push(position);
    return groups;
  }, {});
}

/**
 * Extracts unique departments from positions
 * @param {array} positions - Array of position objects
 * @returns {array} Array of unique departments with 'Alle' at the beginning
 */
export function extractDepartments(positions) {
  if (!positions || positions.length === 0) {
    return ['Alle'];
  }
  
  const departments = new Set(positions.map(pos => pos.department));
  return ['Alle', ...[...departments].sort()];
}

/**
 * Shortens position names for display
 * @param {string} name - Full position name
 * @returns {string} Shortened name
 */
export function shortenPositionName(name) {
  if (!name) return '';
  
  // Extract level and number first
  const levelMatch = name.match(/(Junior|Senior|Principal|Staff|Head|Specialist)\s*(\d+)?/i);
  const departmentMatch = name.match(/(Software Engineer|UX & UI Designer|UX-UI Designer|Marketing & Sales|People|CEO|Teamassistenz)/i);
  
  if (levelMatch && departmentMatch) {
    const level = levelMatch[1];
    const number = levelMatch[2] || '';
    const dept = departmentMatch[1];
    
    // Shorten department names
    let shortDept = dept;
    if (dept.includes('Software Engineer')) shortDept = 'SE';
    else if (dept.includes('UX') && dept.includes('UI')) shortDept = 'UX/UI';
    else if (dept.includes('Marketing')) shortDept = 'M&S';
    else if (dept === 'People') shortDept = 'HR';
    
    // Return level first, then department
    return `${level}${number ? ' ' + number : ''} - ${shortDept}`;
  }
  
  // Special cases
  if (name.includes('CEO')) return 'CEO';
  if (name.includes('Teamassistenz')) return 'Team Assist.';
  if (name.includes('HR Business Partner')) return 'HR Partner';
  
  // Fallback: Take first 2 words
  return name.split(' ').slice(0, 2).join(' ');
}

/**
 * Creates a medium-length position name for tooltips and detailed views
 * @param {string} name - Full position name
 * @returns {string} Medium-length name
 */
export function getMediumPositionName(name) {
  if (!name) return '';
  
  // Extract level and number
  const levelMatch = name.match(/(Junior|Senior|Principal|Staff|Head|Specialist)\s*(\d+)?/i);
  const departmentMatch = name.match(/(Software Engineer|UX & UI Designer|UX-UI Designer|Marketing & Sales|People|CEO|Teamassistenz)/i);
  
  if (levelMatch && departmentMatch) {
    const level = levelMatch[1];
    const number = levelMatch[2] || '';
    const dept = departmentMatch[1];
    
    // Return level first, then full department
    return `${level}${number ? ' ' + number : ''} - ${dept}`;
  }
  
  return name;
}

/**
 * Calculates salary differences between positions
 * @param {object} position1 - First position
 * @param {object} position2 - Second position
 * @returns {object} Salary comparison metrics
 */
export function calculateSalaryDifference(position1, position2) {
  const currentDiffPercent = ((position2.salary.peerigonCurrentBasic / position1.salary.peerigonCurrentBasic) - 1) * 100;
  const currentDiffEuro = position2.salary.peerigonCurrentBasic - position1.salary.peerigonCurrentBasic;
  
  const marketDiffPercent = ((position2.salary.medianBasic / position1.salary.medianBasic) - 1) * 100;
  const marketDiffEuro = position2.salary.medianBasic - position1.salary.medianBasic;
  
  return {
    currentDiffPercent,
    currentDiffEuro,
    marketDiffPercent,
    marketDiffEuro,
    deviationFromMarket: Math.abs(currentDiffPercent - marketDiffPercent)
  };
}